import { z } from 'zod';
import type { translations } from '@/translations/translations';

/** Backend spec 002 — `GET /payments/requests/review-queue` and the validate route. */

export const PAYOUT_REVIEW_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'PAID'] as const;
export const PAYOUT_REVIEW_PAGE_SIZE = 10;
export const PAYOUT_NOTE_MAX_LENGTH = 500;

const amountSchema = z.number().int().nonnegative();

export const reviewQueueItemSchema = z.object({
  id: z.number().int().positive(),
  amount: amountSchema,
  amountPaid: amountSchema,
  amountRemaining: amountSchema,
  network: z.enum(['FLOOZ', 'TMONEY']),
  phone: z.string().min(1).max(32),
  status: z.enum(PAYOUT_REVIEW_STATUSES),
  moderatorNote: z.string().max(PAYOUT_NOTE_MAX_LENGTH).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  requester: z.object({
    id: z.number().int().positive(),
    username: z.string().min(1),
    name: z.string().nullable(),
    country: z.string().nullable(),
  }).strict(),
}).strict();

export const reviewQueuePageSchema = z.object({
  items: z.array(reviewQueueItemSchema).max(50),
  pagination: z.object({
    page: z.number().int().positive(),
    limit: z.number().int().positive().max(50),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().positive(),
  }).strict(),
}).strict();

export const payoutDecisionResultSchema = z.object({
  success: z.literal(true),
  status: z.enum(['APPROVED', 'REJECTED']),
}).strict();

/** A rejection must say why; an approval may carry a note. */
export const payoutDecisionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('APPROVED') }),
  z.object({ action: z.literal('REJECTED'), note: z.string().trim().min(1).max(PAYOUT_NOTE_MAX_LENGTH) }),
]);

export type PayoutReviewStatus = (typeof PAYOUT_REVIEW_STATUSES)[number];
export type ReviewQueueItem = z.infer<typeof reviewQueueItemSchema>;
export type ReviewQueuePage = z.infer<typeof reviewQueuePageSchema>;
export type PayoutDecision = z.infer<typeof payoutDecisionSchema>;
export type ReviewQueueQuery = { status: PayoutReviewStatus; page: number };
export type PayoutReviewCopy = (typeof translations)['fr']['moderatorPanel']['payoutReview'];

export type PayoutReviewErrorKey = 'decisionForbidden' | 'notPending' | 'noteRequired' | 'decisionFailed';

/** HTTP status → translation key, per the outcome table of spec 002 §3.2. */
export function payoutReviewErrorKey(httpStatus: number | undefined): PayoutReviewErrorKey {
  if (httpStatus === 403) return 'decisionForbidden';
  if (httpStatus === 409) return 'notPending';
  if (httpStatus === 400) return 'noteRequired';
  return 'decisionFailed';
}
